import React, { useState } from 'react';

const ProjectTemplates = ({ onClose, onCreateFromTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [category, setCategory] = useState('all');

  const templates = [
    {
      id: 'react-app',
      name: 'React App',
      category: 'frontend',
      description: 'Modern React application with routing, state management, and Tailwind CSS',
      icon: '⚛️',
      features: ['React 18', 'React Router', 'Tailwind CSS', 'Axios', 'ESLint'],
      files: [
        { name: 'package.json', language: 'json', content: `{
  "name": "{{projectName}}",
  "version": "1.0.0",
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-router-dom": "^6.11.0",
    "axios": "^1.4.0"
  },
  "scripts": {
    "start": "react-scripts start",
    "build": "react-scripts build",
    "test": "react-scripts test"
  }
}` },
        { name: 'src/App.js', language: 'javascript', content: `import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

function App() {
  return (
    <Router>
      <div className="App">
        <h1>Welcome to {{projectName}}</h1>
      </div>
    </Router>
  );
}

export default App;` },
        { name: 'src/index.js', language: 'javascript', content: `import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);` },
        { name: 'README.md', language: 'markdown', content: `# {{projectName}}

A modern React application.

## Getting Started

\`\`\`bash
npm install
npm start
\`\`\`

## Build

\`\`\`bash
npm run build
\`\`\`` }
      ]
    },
    {
      id: 'fastapi-backend',
      name: 'FastAPI Backend',
      category: 'backend',
      description: 'Python FastAPI REST API with MongoDB and authentication',
      icon: '🐍',
      features: ['FastAPI', 'MongoDB', 'JWT Auth', 'Async', 'OpenAPI Docs'],
      files: [
        { name: 'requirements.txt', language: 'text', content: `fastapi==0.110.1
uvicorn==0.25.0
motor==3.3.1
pydantic==2.5.0
python-jose==3.5.0
passlib==1.7.4
python-multipart==0.0.6` },
        { name: 'main.py', language: 'python', content: `from fastapi import FastAPI
from fastapi.middleware.cors import CORSMiddleware

app = FastAPI(title="{{projectName}} API")

app.add_middleware(
    CORSMiddleware,
    allow_origins=["*"],
    allow_credentials=True,
    allow_methods=["*"],
    allow_headers=["*"],
)

@app.get("/")
async def root():
    return {"message": "Welcome to {{projectName}}"}

@app.get("/health")
async def health_check():
    return {"status": "healthy"}

if __name__ == "__main__":
    import uvicorn
    uvicorn.run(app, host="0.0.0.0", port=8000)` },
        { name: 'README.md', language: 'markdown', content: `# {{projectName}}

FastAPI backend application.

## Installation

\`\`\`bash
pip install -r requirements.txt
\`\`\`

## Run

\`\`\`bash
uvicorn main:app --reload
\`\`\`

API docs: http://localhost:8000/docs` }
      ]
    },
    {
      id: 'fullstack-app',
      name: 'Full-Stack App',
      category: 'fullstack',
      description: 'Complete full-stack application with React frontend and FastAPI backend',
      icon: '🚀',
      features: ['React', 'FastAPI', 'MongoDB', 'Auth', 'Docker'],
      files: [
        { name: 'frontend/package.json', language: 'json', content: `{
  "name": "{{projectName}}-frontend",
  "version": "1.0.0",
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "axios": "^1.4.0"
  },
  "proxy": "http://localhost:8000"
}` },
        { name: 'backend/requirements.txt', language: 'text', content: `fastapi==0.110.1
uvicorn==0.25.0
motor==3.3.1
python-dotenv==1.0.0` },
        { name: 'backend/main.py', language: 'python', content: `from fastapi import FastAPI
from fastapi.middleware.cors import CORSMiddleware

app = FastAPI()

app.add_middleware(
    CORSMiddleware,
    allow_origins=["http://localhost:3000"],
    allow_credentials=True,
    allow_methods=["*"],
    allow_headers=["*"],
)

@app.get("/api/")
async def root():
    return {"message": "{{projectName}} API"}` },
        { name: 'docker-compose.yml', language: 'yaml', content: `version: '3.8'
services:
  backend:
    build: ./backend
    ports:
      - "8000:8000"
    environment:
      - MONGO_URL=mongodb://mongo:27017
  frontend:
    build: ./frontend
    ports:
      - "3000:3000"
  mongo:
    image: mongo:latest
    ports:
      - "27017:27017"` },
        { name: 'README.md', language: 'markdown', content: `# {{projectName}}

Full-stack application.

## Quick Start

\`\`\`bash
docker-compose up
\`\`\`

Frontend: http://localhost:3000
Backend: http://localhost:8000/docs` }
      ]
    },
    {
      id: 'nodejs-express',
      name: 'Node.js Express API',
      category: 'backend',
      description: 'Express.js REST API with MongoDB and JWT authentication',
      icon: '🟢',
      features: ['Express', 'MongoDB', 'JWT', 'REST API', 'Middleware'],
      files: [
        { name: 'package.json', language: 'json', content: `{
  "name": "{{projectName}}",
  "version": "1.0.0",
  "main": "index.js",
  "dependencies": {
    "express": "^4.18.2",
    "mongoose": "^7.0.0",
    "jsonwebtoken": "^9.0.0",
    "bcryptjs": "^2.4.3",
    "dotenv": "^16.0.3",
    "cors": "^2.8.5"
  },
  "scripts": {
    "start": "node index.js",
    "dev": "nodemon index.js"
  }
}` },
        { name: 'index.js', language: 'javascript', content: `const express = require('express');
const cors = require('cors');
require('dotenv').config();

const app = express();

app.use(cors());
app.use(express.json());

app.get('/', (req, res) => {
  res.json({ message: 'Welcome to {{projectName}}' });
});

app.get('/health', (req, res) => {
  res.json({ status: 'healthy' });
});

const PORT = process.env.PORT || 8000;
app.listen(PORT, () => {
  console.log(\`Server running on port \${PORT}\`);
});` },
        { name: '.env', language: 'text', content: `PORT=8000
MONGO_URI=mongodb://localhost:27017/{{projectName}}
JWT_SECRET=your-secret-key` }
      ]
    },
    {
      id: 'nextjs-app',
      name: 'Next.js App',
      category: 'frontend',
      description: 'Next.js application with App Router, TypeScript, and Tailwind',
      icon: '▲',
      features: ['Next.js 14', 'TypeScript', 'App Router', 'Tailwind', 'API Routes'],
      files: [
        { name: 'package.json', language: 'json', content: `{
  "name": "{{projectName}}",
  "version": "1.0.0",
  "scripts": {
    "dev": "next dev",
    "build": "next build",
    "start": "next start"
  },
  "dependencies": {
    "next": "14.0.0",
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}` },
        { name: 'app/page.tsx', language: 'typescript', content: `export default function Home() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center p-24">
      <h1 className="text-4xl font-bold">Welcome to {{projectName}}</h1>
      <p className="mt-4 text-lg">Built with Next.js 14</p>
    </main>
  )
}` },
        { name: 'app/layout.tsx', language: 'typescript', content: `import type { Metadata } from 'next'

export const metadata: Metadata = {
  title: '{{projectName}}',
  description: 'Built with Next.js',
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang="en">
      <body>{children}</body>
    </html>
  )
}` }
      ]
    },
    {
      id: 'landing-page',
      name: 'Landing Page',
      category: 'frontend',
      description: 'Modern responsive landing page with Tailwind CSS',
      icon: '🌐',
      features: ['HTML5', 'Tailwind CSS', 'Responsive', 'Modern Design', 'SEO Ready'],
      files: [
        { name: 'index.html', language: 'html', content: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{projectName}}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gray-50">
    <nav class="bg-white shadow-lg">
        <div class="max-w-7xl mx-auto px-4 py-4">
            <h1 class="text-2xl font-bold text-gray-800">{{projectName}}</h1>
        </div>
    </nav>

    <main class="max-w-7xl mx-auto px-4 py-12">
        <div class="text-center">
            <h2 class="text-5xl font-bold text-gray-900 mb-4">Welcome to {{projectName}}</h2>
            <p class="text-xl text-gray-600 mb-8">Build something amazing</p>
            <button class="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700">
                Get Started
            </button>
        </div>
    </main>
</body>
</html>` },
        { name: 'styles.css', language: 'css', content: `/* Custom styles */
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}` }
      ]
    }
  ];

  const categories = [
    { id: 'all', label: 'All Templates', icon: '📦' },
    { id: 'frontend', label: 'Frontend', icon: '🎨' },
    { id: 'backend', label: 'Backend', icon: '⚙️' },
    { id: 'fullstack', label: 'Full-Stack', icon: '🚀' }
  ];

  const filteredTemplates = category === 'all'
    ? templates
    : templates.filter(t => t.category === category);

  const handleCreate = () => {
    if (!selectedTemplate || !projectName.trim()) {
      alert('Please select a template and enter a project name');
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    // Replace {{projectName}} in all file contents
    const processedFiles = template.files.map(file => ({
      ...file,
      content: file.content.replace(/\{\{projectName\}\}/g, projectName)
    }));

    onCreateFromTemplate({
      name: projectName,
      template: template.id,
      files: processedFiles
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Project Templates</h2>
            <p className="text-sm text-gray-400 mt-1">Bootstrap your project with a pre-configured template</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 hover:bg-gray-800 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Categories */}
        <div className="flex gap-2 p-4 border-b border-gray-800 overflow-x-auto">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                category === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span>{cat.icon}</span>
              <span className="text-sm font-medium">{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template.id)}
                className={`p-6 rounded-lg cursor-pointer transition-all border-2 ${
                  selectedTemplate === template.id
                    ? 'border-blue-500 bg-blue-900 bg-opacity-20'
                    : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                }`}
              >
                <div className="text-4xl mb-3">{template.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                <p className="text-sm text-gray-400 mb-4">{template.description}</p>
                <div className="flex flex-wrap gap-2">
                  {template.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 bg-gray-950">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white placeholder-gray-500"
            />
            <button
              onClick={handleCreate}
              disabled={!selectedTemplate || !projectName.trim()}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded font-semibold transition-all"
            >
              Create Project
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTemplates;
